import { Injectable } from '@angular/core';
import {
  Organization,
  UpdateOrganizationInput,
  UpdateOrganizationMutation,
  UpdateOrganizationMutationVariables,
  ValidateFileInput,
  ValidateFileQuery,
  ValidateFileQueryVariables,
  ValidateFileResponse,
  CreateFileMetadataMutation,
  CreateFileMetadataMutationVariables,
  HashAndEncryptFileMutation,
  HashAndEncryptFileMutationVariables,
  FileMetadata,
  UploadStatus,
  OnFileMetadataRecordUpdateSubscription,
  OnFileMetadataRecordUpdateSubscriptionVariables,
  UpdateFileMetadataMutation,
  UpdateFileMetadataMutationVariables,
  PublishFileMutation,
  PublishFileMutationVariables,
  ProcessResponse,
  UpdateValidationResultsMutation,
  UpdateValidationResultsMutationVariables,
  UpdateValidationResultsInput,
} from '../../../API';
import { UserManagementService } from '../../shared/services/user-management.service';
import { ApiService } from '../../shared/services/api.service';
import { AuthService } from '../../shared/services/auth.service';
import {
  CreateFileMetadata,
  HashAndEncryptFile,
  PublishFile,
  UpdateFileMetadata,
  UpdateOrganization,
  UpdateValidationResults,
} from '../../../graphql/mutations';
import { ValidateFile } from '../../../graphql/queries';
import { uploadData } from 'aws-amplify/storage';
import { FileItem } from '../models/files';
import { onFileMetadataRecordUpdate } from '../../../graphql/subscriptions';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';

@Injectable({
  providedIn: 'root',
})
export class UploadFilesService {
  graphqlSubscription?: Subscription;
  updatedItemSubject = new BehaviorSubject<FileMetadata | null>(null);
  updatedItem$: Observable<FileMetadata | null> = this.updatedItemSubject.asObservable();

  isFileProcessedSubject = new BehaviorSubject<boolean>(false);

  isFileProcessed$ = this.isFileProcessedSubject.asObservable();

  isFilePublishedSubject = new BehaviorSubject<boolean>(false);

  isFilePublished$ = this.isFilePublishedSubject.asObservable();

  isFileUploadedSubject = new BehaviorSubject<boolean>(false);

  isFileUploaded$ = this.isFileUploadedSubject.asObservable();

  constructor(
    private userManagementService: UserManagementService,
    private apiService: ApiService,
    private authService: AuthService,
    private nzMessageService: NzMessageService,
  ) {}

  ngOnDestroy() {
    this.graphqlSubscription?.unsubscribe();
  }

  async uploadFileToS3(
    file: File,
    onProgress?: (progress: { transferredBytes: number; totalBytes: number }) => void,
  ): Promise<string> {
    const stageBucket = this.authService.getStageBucket();
    const orgId = this.userManagementService.getOrgId();
    const identityId = this.userManagementService.userDetails.identityId;
    const s3Key = `${orgId}/${identityId}/${file.name}`;

    try {
      await uploadData({
        path: s3Key,
        data: file,
        options: {
          bucket: {
            bucketName: stageBucket,
            region: 'us-east-1',
          },
          onProgress,
        },
      }).result;

      return s3Key;
    } catch (error: any) {
      console.error(error);
      this.nzMessageService.error('Error while uploading file');
      return error?.message || 'Error while uploading file to s3';
    }
  }

  async updateOrganization(payload: any): Promise<Organization | null> {
    const orgId = this.userManagementService.getOrgId();
    const userEmail = this.userManagementService.getEmail();
    if (!orgId || !userEmail) {
      throw new Error('Unable to save mapping: missing user info.');
    }
    const input: UpdateOrganizationInput = {
      ...payload,
      orgId: orgId,
      lastModifiedBy: userEmail,
    };
    payload.orgId = orgId;
    payload.lastModifiedBy = userEmail;
    const response = await this.apiService.query<UpdateOrganizationMutation, UpdateOrganizationMutationVariables>(
      UpdateOrganization,
      { updateOrgInput: input },
    );
    return response?.data?.UpdateOrganization ?? null;
  }

  async validateFile(input: ValidateFileInput): Promise<ValidateFileResponse | null> {
    const response = await this.apiService.query<ValidateFileQuery, ValidateFileQueryVariables>(ValidateFile, {
      input,
    });

    return response?.data?.ValidateFile ?? null;
  }

  async updateValidationResults(input: UpdateValidationResultsInput): Promise<boolean> {
    const response = await this.apiService.query<
      UpdateValidationResultsMutation,
      UpdateValidationResultsMutationVariables
    >(UpdateValidationResults, { input });

    return response?.data?.UpdateValidationResults?.success ?? false;
  }

  async createFileMetadata(files: FileItem[]): Promise<FileMetadata[] | void> {
    try {
      const { identityId, orgId, email } = this.userManagementService.userDetails;

      // For now as per business requirement, there will only one file
      const requests = files.map((fileItem, index) => {
        const fileSize = fileItem.nzFile?.size || 0;
        const fileName = fileItem.nzFile?.name;
        const { recordCount, headers } = fileItem;

        const { fileName: name, description } = fileItem.fileForm.value;

        return this.apiService.query<CreateFileMetadataMutation, CreateFileMetadataMutationVariables>(
          CreateFileMetadata,
          {
            metadataCreateRequest: {
              fileName: name,
              description: description,
              orgId: this.userManagementService.getOrgId(),
              templateId: `${orgId}_template${index + 1}`,
              s3Key: `${orgId}/${identityId}/${fileName}`,
              headers: headers,
              fileSize: fileSize,
              recordCount: recordCount,
              uploadBy: email,
            },
          },
        );
      });

      const response = await Promise.all(requests);
      const UpdatedResponseArr = response.map((item) => item.data.CreateFileMetadata);
      return UpdatedResponseArr;
    } catch (error: any) {
      console.warn(error);
      throw error;
    }
  }

  async updateFileMetadata(files: FileItem[]): Promise<FileMetadata[] | void> {
    try {
      const { orgId, email } = this.userManagementService.userDetails;

      // For now as per business requirement, there will only one file
      const requests = files.map((fileItem, index) => {
        const fileId = fileItem.fileId || '';
        const uploadStatus = fileItem.uploadStatus;
        const exportStatus = fileItem.exportStatus;

        return this.apiService.query<UpdateFileMetadataMutation, UpdateFileMetadataMutationVariables>(
          UpdateFileMetadata,
          {
            metadataUpdateRequest: {
              orgId: orgId,
              fileId: fileId,
              lastModifiedBy: email,
              uploadStatus: uploadStatus,
              exportStatus: exportStatus,
            },
          },
        );
      });

      const response = await Promise.all(requests);
      const UpdatedResponseArr = response.map((item) => item.data.UpdateFileMetadata);
      return UpdatedResponseArr;
    } catch (error: any) {
      console.warn(error);
    }
  }

  async processFile(files: FileItem[]): Promise<ProcessResponse[] | void> {
    try {
      const { identityId, orgId, email } = this.userManagementService.userDetails;

      // For now as per business requirement, there will only one file
      const requests = files.map((fileItem, index) => {
        const fileName = fileItem.nzFile?.name;
        const fileId = fileItem.fileId;

        return this.apiService.query<HashAndEncryptFileMutation, HashAndEncryptFileMutationVariables>(
          HashAndEncryptFile,
          {
            processRequest: {
              orgId: orgId,
              fileId: fileId || '',
              s3Key: `${orgId}/${identityId}/${fileName}`,
              lastModifiedBy: email,
              uploadStatus: UploadStatus.PROCESSING,
            },
          },
        );
      });

      const response = await Promise.all(requests);
      const HashAndEncryptResponseArr = response.map((item) => item.data.HashAndEncryptFile);
      return HashAndEncryptResponseArr;
    } catch (error) {
      console.warn(error);
    }
  }

  listenRealtimeChanges(): void {
    this.graphqlSubscription = this.apiService
      .subscription<
        OnFileMetadataRecordUpdateSubscription,
        OnFileMetadataRecordUpdateSubscriptionVariables
      >(onFileMetadataRecordUpdate, {})
      .subscribe({
        next: (value) => {
          const updatedFileItem = value.data.onFileMetadataRecordUpdate;
          if (updatedFileItem) {
            this.updatedItemSubject.next(updatedFileItem);
          }
        },
        error: (error: any) => console.warn('Subscription error:', error),
      }) as Subscription;
  }

  getUpdatedItem(): FileMetadata | null {
    return this.updatedItemSubject.value;
  }

  resetFileProcessingState(): void {
    this.isFileProcessedSubject.next(false);
    this.isFilePublishedSubject.next(false);
  }

  announceStateUpdate(newItem: FileMetadata): void {
    const { fileName, uploadStatus, exportStatus } = newItem;
    let msg = '';
    switch (uploadStatus) {
      case UploadStatus.COMPLETED: {
        msg = `Processing for ${fileName} is completed successfully.`;
        this.isFileProcessedSubject.next(true);

        this.nzMessageService.success(msg, {
          nzDuration: 5000,
        });
        break;
      }

      case UploadStatus.FAILED: {
        msg = `Processing failed for ${fileName}. Please try again from the start. If the issue persists, contact the NFL team for assistance.`;
        this.nzMessageService.error(msg, {
          nzDuration: 5000,
        });
        break;
      }
    }
  }

  async publishFile(files: FileItem[]) {
    try {
      const { orgId, email } = this.userManagementService.userDetails;

      // For now as per business requirement, there will only one file
      const requests = files.map((fileItem) => {
        const s3Key = fileItem.s3Key || '';

        return this.apiService.query<PublishFileMutation, PublishFileMutationVariables>(PublishFile, {
          publishRequest: {
            orgId: orgId,
            s3Key: s3Key,
            lastModifiedBy: email,
          },
        });
      });

      await Promise.all(requests);

      const response = await Promise.all(requests);
      const publishResponseArr = response.map((item) => item.data.PublishFile);

      this.isFilePublishedSubject.next(true);
      return publishResponseArr;
    } catch (error) {
      console.warn(error);
      return false;
    }
  }

  get isFileProcessed(): boolean {
    return this.isFileProcessedSubject.value;
  }

  get isFilePublished(): boolean {
    return this.isFilePublishedSubject.value;
  }

  get isFileUploaded(): boolean {
    return this.isFileUploadedSubject.value;
  }
}
